import { service } from '@ember/service';
import Route from '@ember/routing/route';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type TasGroupModel from 'tio-common/models/tas-group';
import type TASProgramTemplateModel from 'tio-common/models/tas-program-template';
import type TasParticipantModel from 'tio-common/models/tas-participant';
import type RouterService from '@ember/routing/router';
import type FeatureService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type ApiService from 'tio-employee/services/api';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-common/models/search';
import {
  type RecentApplication,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  buildType4Instances,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export default class AuthenticatedTasGettingStartedRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeatureService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare api: ApiService;

  async model({ tas_program_template_id }: { tas_program_template_id: string }) {
    // Load the TasParticipant fresh to ensure we alert any HELD participant state
    // Include tas-eligibilities here to avoid duplicate queries
    const tasParticipants = await this.store.query<TasParticipantModel>('tas-participant', {
      filter: {
        employee: this.sessionContext.currentEmployee.id,
      },
      include: 'employee.dependents,tas-eligibilities',
    });

    if (tasParticipants.length === 0) {
      console.warn('No TAS participant found for current employee');
      this.router.transitionTo('authenticated.tas.dashboard');
      return null;
    }

    const tasParticipant = tasParticipants[0];

    // Load eligibilities from the already-fetched participant
    await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee(tasParticipant);

    const tasGroups = await this.store.query<TasGroupModel>('tas-group', {
      filter: {
        company: this.sessionContext.currentEmployee.company.id,
        flavor: 'TAS.GroupFlavor.MUTUALLY_EXCLUSIVE',
      },
      include: 'tas-groupings.tas-program-template',
    });

    const activeInstances = await this.store.query<TasProgramInstanceModel>(
      'tas-program-instance',
      {
        filter: { employee: this.sessionContext.currentEmployee.id, active: true },
        include: 'tas-program-template',
      }
    );

    const programTemplate = await this.store.findRecord<TASProgramTemplateModel>(
      'tas-program-template',
      tas_program_template_id,
      {
        include: 'conditions.public-assets',
      }
    );

    // Fetch recent activity (2 months) and all applications for this program template
    const recentActivity = await this.fetchRecentActivity(programTemplate, false);
    const allApplications = await this.fetchRecentActivity(programTemplate, true);

    return {
      activeInstances: activeInstances,
      programTemplate: programTemplate,
      tasParticipant: tasParticipant,
      tasGroups: tasGroups,
      recentActivity,
      allApplications,
    };
  }

  /**
   * Fetch applications for the specific program template
   * @param programTemplate - The program template to fetch applications for
   * @param showAll - If true, fetches all applications; if false, only past 2 months
   * Returns different data structure based on MULTIPLE_INSTANCE_PROGRAM setting
   */
  async fetchRecentActivity(
    programTemplate: TASProgramTemplateModel,
    showAll = false
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: Array<{
      instanceApplication: RecentApplication;
      childApplications: RecentApplication[];
    }>;
  }> {
    const currentEmployee = this.sessionContext.currentEmployee;
    const companyId = currentEmployee.company?.id;
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    if (!companyId) {
      return { flatApplications: [], programInstances: [] };
    }

    try {
      // Query for applications for this specific program template
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', currentEmployee.id)
        .mustTerm('tas_program_template_id_keyword', programTemplate.id)
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        });

      // Only add date filter if not showing all
      if (!showAll) {
        builder.addFilter({ range: { updated_at: { gte: twoMonthsAgo.toISOString() } } });
      }

      builder.setSort('updated_at:desc').setPagination(1, 200);

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const response = (await this.api.call('/api/v2/search', {
        method: 'POST',
        body: JSON.stringify(query),
      })) as OpenSearchResponse<RecentApplication>;
      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      // Separate instances from applications
      // Type 1 is treated as instance (no children)
      // Type 4.2/4.3 are grouped instances with children
      const type4InstanceMap = new Map<string, RecentApplication[]>();
      const flatApps: RecentApplication[] = [];

      for (const app of allApps) {
        const type = app.tasProgramTemplateTypeKeyword;
        const isInstanceRecord = app.recordType === 'instance';

        // Type 1: Program instance with no children - treat as instance
        if (type === 'TAS.ProgramType.1') {
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        }
        // Type 4.2/4.3 templates: Group instances with their applications
        else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          isInstanceRecord
        ) {
          // This is a Type 4 instance record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);

          // OpenSearch may embed child application data in tasApplicationFields[] instead
          // of returning separate recordType='application' documents. Extract those as
          // synthetic child records so buildType4Instances can group them properly.
          const rawApp = app as unknown as Record<string, unknown>;
          const embeddedApps = rawApp['tasApplicationFields'] as
            | Record<string, unknown>[]
            | undefined;
          if (embeddedApps?.length) {
            for (const embedded of embeddedApps) {
              type4InstanceMap.get(instanceId)!.push({
                ...app,
                ...(embedded as Partial<RecentApplication>),
                recordType: 'application',
              });
            }
          }
        } else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          !isInstanceRecord
        ) {
          // This is a Type 4.2/4.3 application record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        } else {
          // Type 2/3: Application data is nested in tasApplicationFields[] array,
          // not at the root level. Extract and merge application fields so the
          // component can render with correct ID, state, name, and payment data.
          const rawApp = app as unknown as Record<string, unknown>;
          const appFields = rawApp['tasApplicationFields'] as Record<string, unknown>[] | undefined;

          if (appFields?.length) {
            const firstApp = appFields[0];

            if (firstApp) {
              flatApps.push({
                ...app,
                ...(firstApp as Partial<RecentApplication>),
              });
            }
          } else if (app.tasApplicationIdKeyword) {
            // Real application document without embedded tasApplicationFields — keep as-is
            flatApps.push(app);
          }
          // Skip instance-only records with no applications (avoids phantom rows)
        }
      }

      // Deduplicate Type 4 child apps: if both a separate application document and an
      // embedded tasApplicationFields[] entry exist for the same application ID, keep
      // only the separate document (it has richer/more accurate data).
      for (const [mapKey, records] of type4InstanceMap) {
        // Find the actual instance document's id to distinguish embedded records
        const instanceRecord = records.find((r) => r.recordType === 'instance');
        const instanceDocId = instanceRecord?.id;

        // Only consider records with their own unique document id as "real"
        // application docs. Synthetic records created from embedded
        // tasApplicationFields[] share the instance document's id and should NOT count.
        const realAppIds = new Set(
          records
            .filter(
              (r) =>
                r.recordType !== 'instance' &&
                r.tasApplicationIdKeyword &&
                (!instanceDocId || r.id !== instanceDocId)
            )
            .map((r) => r.tasApplicationIdKeyword)
        );
        if (realAppIds.size > 0) {
          const deduped = records.filter((r) => {
            if (r.recordType === 'instance') return true;
            if (!instanceDocId || r.id !== instanceDocId) return true;
            return !realAppIds.has(r.tasApplicationIdKeyword);
          });
          type4InstanceMap.set(mapKey, deduped);
        }
      }

      const programInstances = buildType4Instances(type4InstanceMap);

      return { flatApplications: flatApps, programInstances };
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      return { flatApplications: [], programInstances: [] };
    }
  }
}
